/**
 * Edit App Manifest page.
 *
 * Route: /admin/app-catalogue/edit/:appId
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft } from '@tabler/icons-react';
import { Stack, Group, Title, Text, Button, Alert, Loader, Center, toast } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';
import { AppManifestForm } from './AppManifestForm';

const CATALOGUE_QUERY_KEY = ['admin', 'app-catalogue', 'all'];

export default function EditAppPage() {
  const navigate = useNavigate();
  const { appId = '' } = useParams<{ appId: string }>();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const [saving, setSaving] = useState(false);

  // The admin list is the source of truth — load the full unfiltered set and
  // pick the manifest being edited so a disabled app is still editable.
  const {
    data: apps = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const manifest = apps.find((a) => a.appId === appId);

  const handleSubmit = async (updated: AppManifest) => {
    setSaving(true);
    try {
      await catalogueService.updateApp(appId, updated);
      await queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY });
      toast.success(`${updated.displayName} updated`);
      navigate(ADMIN_ROUTES.APP_CATALOGUE_APPS);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update app manifest');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_APPS)}
            >
              Back to catalogue
            </Button>
            <Title order={2} mt="xs">
              Edit App Manifest
            </Title>
            <Text c="dimmed" size="sm">
              {appId}
            </Text>
          </div>
        </Group>

        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : error ? (
          <Alert color="red" title="Service Unavailable">
            {error instanceof Error ? error.message : 'Failed to load the manifest.'}
          </Alert>
        ) : !manifest ? (
          <Alert color="yellow" title="App not found">
            No manifest with id <strong>{appId}</strong> exists in the catalogue.
          </Alert>
        ) : (
          <AppManifestForm
            initial={manifest}
            saving={saving}
            onSubmit={handleSubmit}
            onCancel={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_APPS)}
          />
        )}
      </Stack>
    </FadeIn>
  );
}
